import { memo, ReactElement, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { mountPoint } from 'utils/generic/route-manipulation';
import { isMobile } from 'utils/is-mobile';
import { Competition, Region, Sport } from 'interfaces/sportsbook-data-levels';
import { AdvancedShowAllEventsUnderlined } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedShowAllEventsUnderlined';
import { ConditionalWrapper } from 'components/ConditionalWrapper';
import { GlobalIcon } from 'components/GlobalIcon';
import { NavigationCollapse } from 'components/NavigationCollapse';
import { Portal } from 'components/Shared/Portal';
import { CompetitionFavoriteMatchesCountIndicator } from 'newcomponents/Shared/sportsbook/CompetitionFavoriteMatchesCountIndicator';
import { CompetitionsFavorite } from 'newcomponents/Shared/sportsbook/CompetitionsFavorite';
import { LiveIndicator } from 'newcomponents/Shared/sportsbook/LiveIndicator';
import { Button } from 'newcomponents/UI/Button';
import { Checkbox } from 'newcomponents/UI/Checkbox';
import { useCalculateBetslipFixedSwitchersHeight } from 'hooks/betslip/useCalculateBetslipFixedSwitchersHeight';
import { useSearchParams } from 'hooks/useSearchParams';
import { useCustomHistory } from 'newhooks/generic/useCustomHistory';
import { setAdvancedTempCompetitionIds } from 'store/actions/new-sportsbook';
import { getAdvancedTempCompetitionIds } from 'store/selectors/new-sportsbook';
import './index.less';

export enum EShowEventsButtonLayout {
  BUTTON,
  UNDERLINE
}

type TAdvancedLeftMenuProps = {
  sportsData: Sport[];
  portalRootId: string;
  showEventsButtonType?: EShowEventsButtonLayout;
  showSportCollapse?: boolean;
  showAllEventsExtraClassname?: string;
};

export const AdvancedSportsContent = ({
  sportsData,
  showSportCollapse,
  portalRootId,
  showEventsButtonType = EShowEventsButtonLayout.BUTTON,
  showAllEventsExtraClassname
}: TAdvancedLeftMenuProps): ReactElement => {
  const dispatch = useDispatch();
  const { push } = useCustomHistory();
  const { t } = useTranslation();

  const selectedCompetitionIds = useSelector(getAdvancedTempCompetitionIds);

  const { compIds: idsInQuery } = useSearchParams();
  const bottomNavSwitchersHeight = useCalculateBetslipFixedSwitchersHeight();

  useLayoutEffect(() => {
    if (idsInQuery && !selectedCompetitionIds.length) {
      dispatch(setAdvancedTempCompetitionIds(idsInQuery.split(',')));
    }

    if (isMobile()) {
      return () => {
        dispatch(setAdvancedTempCompetitionIds([]));
      };
    }
  }, []);

  const onShowEventsCLick = () => {
    const idsToSetInQuery = selectedCompetitionIds.join(',');

    if (idsToSetInQuery !== idsInQuery) {
      const redirectPath = `${mountPoint(
        window.getPathname()
      )}?compIds=${idsToSetInQuery}`;

      push(redirectPath);
    }
  };

  return (
    <div className="advancedSportsContent">
      {sportsData.map(sportData => (
        <SportCollapse
          key={sportData.id}
          sportData={sportData}
          selectedCompetitionIds={selectedCompetitionIds}
          showSportCollapse={showSportCollapse}
        />
      ))}
      {selectedCompetitionIds.length ? (
        <Portal rootId={portalRootId}>
          {showEventsButtonType === EShowEventsButtonLayout.BUTTON ? (
            <div
              {...(isMobile() && {
                style: { bottom: bottomNavSwitchersHeight - 1 }
              })}
              className={
                showAllEventsExtraClassname ||
                'advancedSportsContent__showEvents'
              }
            >
              <Button fullwidth type="primary" onClick={onShowEventsCLick}>
                {t('sportsbook.showAllEvents')}
              </Button>
            </div>
          ) : (
            <AdvancedShowAllEventsUnderlined
              onClick={onShowEventsCLick}
              className={showAllEventsExtraClassname}
              bottomNavSwitchersHeight={bottomNavSwitchersHeight}
            />
          )}
        </Portal>
      ) : null}
    </div>
  );
};

type TSportCollapseProps = {
  sportData: Sport;
  selectedCompetitionIds: string[];
  showSportCollapse?: boolean;
};

const SportCollapse = memo(
  ({
    sportData,
    selectedCompetitionIds,
    showSportCollapse = true
  }: TSportCollapseProps): ReactElement => {
    const liveGameIdsByRegions = useMemo(() => {
      const result: Record<string, number[]> = {};

      sportData?.region &&
        Object.values(sportData.region).forEach(region => {
          region?.competition &&
            Object.values(region.competition).forEach(comp => {
              comp?.game &&
                Object.values(comp.game).forEach(game => {
                  if (game?.type === 1) {
                    result[region.alias] = [
                      ...(result[region.alias] || []),
                      comp.id
                    ];
                  }
                });
            });
        });

      return result;
    }, [sportData.region]);

    const sportHasLiveGames = !!Object.values(liveGameIdsByRegions).length;

    return (
      <ConditionalWrapper
        condition={showSportCollapse}
        wrapper={children => (
          <NavigationCollapse
            defaultActive={SpringConfigs.MOCKED_DATA}
            wrapperClassName="mainCollapseWrapper"
            headerClassName="mainCollapseHeader"
            title={sportData.name}
            key={sportData.id}
            icon={
              <GlobalIcon
                lib="sports"
                name={sportData.alias || ''}
                size={EIconSize._24}
              />
            }
            {...(sportHasLiveGames && { extraRight: <LiveIndicator /> })}
            dataTestId={`${sportData.name.toLowerCase()}`}
          >
            {children}
          </NavigationCollapse>
        )}
      >
        <div className="advancedSportsContent__regionsWrapper">
          {sportData.region &&
            sortByOrder(Object.values(sportData.region)).map(region =>
              region?.competition && Object.keys(region?.competition).length ? (
                <RegionCollapse
                  key={region.id}
                  regionData={region}
                  sportAlias={sportData.alias}
                  selectedCompetitionIds={selectedCompetitionIds}
                  compIdsWithLiveGame={liveGameIdsByRegions[region.alias]}
                />
              ) : null
            )}
        </div>
      </ConditionalWrapper>
    );
  }
);

type TRegionCollapseProps = {
  regionData: Region;
  sportAlias: string;
  compIdsWithLiveGame: number[];
  selectedCompetitionIds: string[];
};

const RegionCollapse = memo(
  ({
    regionData,
    sportAlias,
    selectedCompetitionIds,
    compIdsWithLiveGame
  }: TRegionCollapseProps): ReactElement | null => {
    const dispatch = useDispatch();
    const allCompIds =
      regionData.competition && Object.keys(regionData.competition);

    const isAllSelected = allCompIds?.every(id =>
      selectedCompetitionIds?.includes(id)
    );

    return allCompIds?.length ? (
      <NavigationCollapse
        wrapperClassName="innerCollapseWrapper"
        headerClassName="innerCollapseHeader"
        title={regionData.name}
        key={regionData.id}
        dataTestId="region"
        icon={
          <GlobalIcon
            lib="flags"
            name={regionData.alias || ''}
            size={EIconSize._24}
          />
        }
        extraLeft={
          <Checkbox
            checked={isAllSelected}
            onClick={e => {
              e.stopPropagation();
              let newCompIds = new Set(selectedCompetitionIds);

              if (isAllSelected) {
                allCompIds?.forEach(id => newCompIds.delete(id));
              } else {
                newCompIds = new Set([...newCompIds, ...allCompIds]);
              }

              dispatch(setAdvancedTempCompetitionIds(Array.from(newCompIds)));
            }}
          />
        }
        {...(!!compIdsWithLiveGame?.length && {
          extraRight: <LiveIndicator />
        })}
      >
        <div className="competitionsSelect">
          {regionData.competition &&
            sortByOrder(Object.values(regionData.competition)).map(comp => (
              <CompetitionRow
                key={comp.id}
                comp={comp}
                selectedCompetitionIds={selectedCompetitionIds}
                sportAlias={sportAlias}
                regionAlias={regionData.alias}
                compIdsWithLiveGame={compIdsWithLiveGame}
              />
            ))}
        </div>
      </NavigationCollapse>
    ) : null;
  }
);

type TCompetitionRowProps = {
  comp: Competition;
  sportAlias: string;
  regionAlias: string;
  compIdsWithLiveGame: number[];
  selectedCompetitionIds: string[];
};

const CompetitionRow = ({
  comp,
  selectedCompetitionIds,
  sportAlias,
  regionAlias,
  compIdsWithLiveGame
}: TCompetitionRowProps) => {
  const dispatch = useDispatch();

  return (
    <div
      key={comp.id}
      className="competitionsSelect__item"
      onClick={() => {
        const newCompIds = new Set(selectedCompetitionIds);

        if (newCompIds.has(String(comp.id))) {
          newCompIds.delete(String(comp.id));
        } else {
          newCompIds.add(String(comp.id));
        }

        dispatch(
          setAdvancedTempCompetitionIds(Array.from(Array.from(newCompIds)))
        );
      }}
      data-testid="competition"
    >
      <Checkbox checked={selectedCompetitionIds?.includes(String(comp.id))} />
      <CompetitionsFavorite
        iconSize={EIconSize._16}
        competitionInfo={{
          name: comp.name,
          id: comp.id,
          regionAlias,
          sportAlias
        }}
      />
      <span className="competitionsSelect__item__name">{comp.name}</span>
      {compIdsWithLiveGame?.includes(comp.id) ? <LiveIndicator /> : null}
      <CompetitionFavoriteMatchesCountIndicator competitionId={comp.id} />
    </div>
  );
};
